import { getIcon } from '../../utils/iconMap';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import GnomeChat from './GnomeChat';
import SafeIcon from '../../common/SafeIcon';
import { getGnomeName } from '../../utils/gnomeUtils';
import { FiActivity, FiMic, FiMicOff, FiRefreshCw, FiSettings, FiTrash2, FiVolume2, FiVolumeX, FiX } from 'react-icons/fi';
import { GiPointyHat } from 'react-icons/gi';
import { supabase } from '../../supabase';
import { toast } from 'sonner';
import { useWorkspace } from '../../context/WorkspaceContext';

const GnomeChatModal = React.memo(({ isOpen, onClose, gnome }) => {
    const { activeWorkspace } = useWorkspace();
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [autoSpeakEnabled, setAutoSpeakEnabled] = useState(false);
    const [isListeningEnabled, setIsListeningEnabled] = useState(false);
    const chatRef = useRef(null);

    // Sync autoSpeakEnabled and listening state with GnomeChat state
    useEffect(() => {
        if (chatRef.current) {
            setAutoSpeakEnabled(chatRef.current.getAutoSpeakState());
            setIsListeningEnabled(chatRef.current.getListeningState?.() || false);
        }
    }, [refreshTrigger, chatRef]);

    const toggleModalAutoSpeak = () => {
        if (chatRef.current) {
            chatRef.current.toggleAutoSpeak();
            // toggleAutoSpeak is async so we flip our local state immediately
            setAutoSpeakEnabled(prev => !prev);
        }
    };

    const toggleModalListening = () => {
        if (chatRef.current?.toggleListening) {
            chatRef.current.toggleListening();
            setIsListeningEnabled(prev => !prev);
        }
    };

    // We need to pass the gnome object.
    if (!gnome || !isOpen) return null;

    // Resolve icon string to component
    let IconComponent = GiPointyHat;
    if (gnome.icon) {
        // If it's already a component (unlikely from DB but possible from objects), use it
        if (typeof gnome.icon !== 'string') {
             IconComponent = gnome.icon;
        } else {
             IconComponent = getIcon(gnome.icon) || getIcon(gnome.icon) || IconComponent;
        }
    }

    // Create a gnome object with resolved icon for GnomeChat
    const resolvedGnome = { ...gnome, icon: IconComponent };

    const handleClearChat = async () => {
        if (!activeWorkspace || !gnome) return;
        if (!window.confirm("Are you sure you want to clear this chat history?")) return;

        try {
            const { error } = await supabase
                .from('chat_messages')
                .delete()
                .eq('workspace_id', activeWorkspace.id)
                .eq('gnome_id', gnome.id);

            if (error) throw error;
            toast.success("Chat history cleared.");
            // Trigger refresh in GnomeChat
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error("Error clearing chat:", err);
            toast.error("Failed to clear chat.");
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
             {/* Click outside to close */}
             <div className="absolute inset-0" onClick={onClose}></div>

             <div className="bg-white dark:bg-charcoal-900 w-full max-w-2xl h-[600px] max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-charcoal-200 dark:border-charcoal-800 z-10 relative">

                {/* Header */}
                <div className="shrink-0 flex items-center justify-between p-4 border-b border-charcoal-100 dark:border-charcoal-800 bg-charcoal-50 dark:bg-charcoal-900">
                    <div className="flex items-center gap-3">
                         <div className={`w-10 h-10 rounded-full ${gnome.color} flex items-center justify-center text-white shadow-sm overflow-hidden`}>
                            {gnome.id === '0' || gnome.id === 0 ? (
                                <img src="/shroom-logo.webp" alt="Team" className="w-full h-full object-cover" />
                            ) : gnome.headshot ? (
                                <img src={gnome.headshot} alt={gnome.title} className="w-full h-full object-cover" />
                            ) : (
                                <SafeIcon icon={IconComponent} />
                            )}
                         </div>
                         <div>
                             <h3 className="text-lg font-bold text-charcoal-900 dark:text-white">
                                {getGnomeName(gnome.title)}
                            </h3>
                            <p className="text-xs text-charcoal-500 dark:text-charcoal-400">{gnome.focus}</p>
                         </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Microphone Toggle Button */}
                        <button
                            type="button"
                            onClick={toggleModalListening}
                            className={`p-2 rounded-lg transition-colors ${isListeningEnabled ? 'text-red-500 bg-red-50 dark:bg-red-900/20 animate-pulse' : 'text-charcoal-400 hover:text-charcoal-600 hover:bg-charcoal-200 dark:hover:bg-charcoal-800'}`}
                            aria-label={isListeningEnabled ? "Stop listening" : "Start voice input"}
                            title={isListeningEnabled ? "Stop listening" : "Start voice input"}
                        >
                            <SafeIcon icon={isListeningEnabled ? FiMicOff : FiMic} className="text-lg" />
                        </button>

                        {/* Auto-Speak Toggle Button */}
                        <button
                            type="button"
                            onClick={toggleModalAutoSpeak}
                            className={`p-2 rounded-lg transition-colors ${autoSpeakEnabled ? 'text-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'text-charcoal-400 hover:text-charcoal-600 hover:bg-charcoal-200 dark:hover:bg-charcoal-800'}`}
                            aria-label={autoSpeakEnabled ? "Disable auto-read" : "Enable auto-read responses"}
                            title={autoSpeakEnabled ? "Disable auto-read" : "Enable auto-read responses"}
                        >
                            <SafeIcon icon={autoSpeakEnabled ? FiVolume2 : FiVolumeX} className="text-lg" />
                        </button>

                        {/* Settings Button */}
                        <button
                            type="button"
                            onClick={() => chatRef.current?.openSettings()}
                            className="p-2 rounded-lg hover:bg-charcoal-200 dark:hover:bg-charcoal-800 text-charcoal-400 hover:text-charcoal-600 transition-colors"
                            title="Gnome Settings (Mode & Guardrails)"
                            aria-label="Gnome Settings (Mode & Guardrails)"
                        >
                            <SafeIcon icon={FiSettings} className="text-lg" />
                        </button>

                        {/* Refresh Button */}
                        <button
                            type="button"
                            onClick={() => setRefreshTrigger(prev => prev + 1)}
                            className="p-2 rounded-lg hover:bg-charcoal-200 dark:hover:bg-charcoal-800 text-charcoal-400 hover:text-charcoal-600 transition-colors"
                            title="Refresh Chat"
                            aria-label="Refresh Chat"
                        >
                            <SafeIcon icon={FiRefreshCw} className="text-lg" />
                        </button>

                        {/* Diagnostics Button */}
                        <button
                            type="button"
                            onClick={() => chatRef.current?.runDiagnostics()}
                            className="p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-charcoal-400 hover:text-blue-500 transition-colors"
                            title="System Status / Diagnostics"
                            aria-label="System Status / Diagnostics"
                        >
                            <SafeIcon icon={FiActivity} className="text-lg" />
                        </button>

                        {/* Clear Chat Button */}
                        <button
                            type="button"
                            onClick={handleClearChat}
                            className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-charcoal-400 hover:text-red-500 transition-colors"
                            title="Clear Chat History"
                            aria-label="Clear Chat History"
                        >
                            <SafeIcon icon={FiTrash2} className="text-lg" />
                        </button>

                        <button
                            type="button"
                            className="p-2 rounded-lg hover:bg-charcoal-200 dark:hover:bg-charcoal-800 text-charcoal-400 hover:text-charcoal-600 transition-colors"
                            onClick={onClose}
                            aria-label="Close Modal"
                        >
                            <SafeIcon icon={FiX} className="text-xl" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden p-0 relative h-full">
                    <GnomeChat
                        key={`${activeWorkspace?.id}-${resolvedGnome?.id}`}
                        ref={chatRef}
                        gnome={resolvedGnome}
                        hideHeader={true}
                        refreshTrigger={refreshTrigger}
                        onListeningChange={setIsListeningEnabled}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
});

export default GnomeChatModal;
